/**
 * Task Detail Dialog Component
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Brain, Zap, CheckCircle2, Play, XCircle, Code2, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PriorityColors, StatusColors } from "./constants";

export function TaskDetailDialog({ task, logs, onClose, onApprove, onDismiss, onAnalyze, onExecute, adminAxios }) {
  const [promptText, setPromptText] = useState(null);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!task) return null;

  const analysis = task.analysis || {};
  const suggestion = task.suggestion || {};

  const handleGeneratePrompt = async () => {
    setLoadingPrompt(true);
    try {
      const res = await adminAxios.get(`/command-center/tasks/${task.task_id}/claude-code-prompt`);
      setPromptText(res.data.prompt);
    } catch {
      toast.error("Erro ao gerar prompt para Claude Code");
    }
    setLoadingPrompt(false);
  };

  const handleCopy = () => {
    if (!promptText) return;
    navigator.clipboard.writeText(promptText).then(() => {
      setCopied(true);
      toast.success("Prompt copiado! Cole no Claude Code.");
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Badge className={PriorityColors[task.priority]}>{task.priority}</Badge>
            <Badge className={StatusColors[task.status]}>{task.status}</Badge>
          </div>
          <DialogTitle className="text-xl mt-2">{task.title}</DialogTitle>
          <DialogDescription>{task.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Meta */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Agente:</span>
              <span className="ml-2 font-medium capitalize">{task.agent}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Tipo:</span>
              <span className="ml-2 font-medium">{task.type}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Restaurante:</span>
              <span className="ml-2 font-medium">{task.restaurant_name || "Global"}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Criado:</span>
              <span className="ml-2">{new Date(task.created_at).toLocaleString()}</span>
            </div>
          </div>

          {/* Analysis */}
          {analysis.diagnosis && (
            <div className="p-4 bg-muted/30 rounded-lg">
              <h4 className="font-medium flex items-center gap-2 mb-2">
                <Brain className="w-4 h-4" />
                Diagnostico
                {analysis.used_llm && <Badge variant="outline" className="text-xs">IA</Badge>}
              </h4>
              <p className="text-sm text-muted-foreground">{analysis.diagnosis}</p>
              {analysis.confidence > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Confianca: {(analysis.confidence * 100).toFixed(0)}%
                </p>
              )}
            </div>
          )}

          {/* Suggestion */}
          {suggestion.action && (
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <h4 className="font-medium flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-primary" />
                Acao Sugerida
              </h4>
              <p className="text-sm font-mono bg-muted p-2 rounded">{suggestion.action}</p>
              {suggestion.reason && (
                <p className="text-sm text-muted-foreground mt-2">{suggestion.reason}</p>
              )}
              <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                <span>Risco: {suggestion.risk_level}</span>
                <span>Reversivel: {suggestion.reversible ? "Sim" : "Nao"}</span>
              </div>
            </div>
          )}

          {/* Claude Code Prompt */}
          <div className="border border-dashed border-indigo-200 rounded-lg p-4 bg-indigo-50/40">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium flex items-center gap-2 text-indigo-800">
                <Code2 className="w-4 h-4" />
                Prompt para Claude Code
              </h4>
              <div className="flex gap-2">
                {!promptText && (
                  <Button size="sm" variant="outline" onClick={handleGeneratePrompt} disabled={loadingPrompt}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-100">
                    {loadingPrompt
                      ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />A gerar...</>
                      : <><Code2 className="w-3.5 h-3.5 mr-1" />Gerar Prompt</>}
                  </Button>
                )}
                {promptText && (
                  <Button size="sm" variant="outline" onClick={handleCopy}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-100">
                    {copied
                      ? <><Check className="w-3.5 h-3.5 mr-1 text-green-600" />Copiado!</>
                      : <><Copy className="w-3.5 h-3.5 mr-1" />Copiar</>}
                  </Button>
                )}
              </div>
            </div>
            {promptText ? (
              <pre className="text-xs bg-white border border-indigo-100 rounded p-3 overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap text-gray-700 font-mono">
                {promptText}
              </pre>
            ) : (
              <p className="text-xs text-indigo-600">
                Clique em "Gerar Prompt" para criar o prompt estruturado para colar no Claude Code.
                O Claude Code analisa o repositorio, propoe o patch — humano aprova antes do deploy.
              </p>
            )}
          </div>

          {/* Logs */}
          {logs && logs.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Historico</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {logs.map((log) => (
                  <div key={log.log_id} className="text-xs p-2 bg-muted/30 rounded flex justify-between">
                    <span className="capitalize">{log.action_type}</span>
                    <span className="text-muted-foreground">{new Date(log.timestamp).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {task.status === "pending" && (
            <Button variant="outline" onClick={() => onAnalyze(task.task_id)}>
              <Brain className="w-4 h-4 mr-2" />
              Analisar com IA
            </Button>
          )}
          {task.status === "suggested" && (
            <Button onClick={() => onApprove(task.task_id)}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Aprovar
            </Button>
          )}
          {task.status === "approved" && (
            <Button onClick={() => onExecute(task.task_id)} className="bg-green-600 hover:bg-green-700">
              <Play className="w-4 h-4 mr-2" />
              Executar Acao
            </Button>
          )}
          {["pending", "analyzing", "suggested"].includes(task.status) && (
            <Button variant="destructive" onClick={() => onDismiss(task.task_id)}>
              <XCircle className="w-4 h-4 mr-2" />
              Descartar
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
